import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { api, callableErrorMessage } from "../lib/api";
import { BUSINESS_TYPES, validateTaxId } from "@loyalty/shared";
import { track } from "../lib/analytics";
import { useGoogleOneTap } from "../lib/googleOneTap";

type Step = 1 | 2 | 3;

export function Signup() {
  const nav = useNavigate();
  const { user, refreshClaims } = useAuth();
  // Activamos One-Tap en step 3 (cuando va a loguearse) y si todavía no hay user.
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useGoogleOneTap(!user && step === 3);

  // Pre-fill desde URL params (?rut=...&nombre=...&email=...&tipo=...&ref=...)
  const [country] = useState<"CL" | "AR">("CL");
  const [personType, setPersonType] = useState<"natural" | "juridica">("juridica");
  const [taxId, setTaxId] = useState(params.get("rut") ?? "");
  const [businessType, setBusinessType] = useState(params.get("tipo") ?? "cafeteria");
  const [name, setName] = useState(params.get("nombre") ?? "");
  const [legalName, setLegalName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [website, setWebsite] = useState("");
  const [instagram, setInstagram] = useState("");
  const [locations, setLocations] = useState<"1" | "2-5" | "6+">("1");
  const [ownerRole, setOwnerRole] = useState("Dueño");
  const [ownerName, setOwnerName] = useState("");
  const [phone, setPhone] = useState("");
  const refSource = params.get("ref") ?? undefined;

  const taxIdFormat = country === "CL" ? "CL-RUT" : "AR-CUIT";
  const taxValidation = useMemo(
    () => (taxId ? validateTaxId(taxId, taxIdFormat) : null),
    [taxId, taxIdFormat]
  );

  useEffect(() => {
    if (user?.displayName && !ownerName) setOwnerName(user.displayName);
  }, [user, ownerName]);

  // Track entrada al funnel + parámetros de attribution.
  useEffect(() => {
    track("signup_started", {
      ref_source: refSource ?? "direct",
      prefilled_rut: !!params.get("rut"),
      prefilled_nombre: !!params.get("nombre"),
      prefilled_tipo: !!params.get("tipo")
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canAdvance = (s: Step): boolean => {
    if (s === 1) {
      return !!taxValidation?.valid && !!businessType;
    }
    if (s === 2) {
      return name.trim().length >= 2 && address.trim().length >= 4;
    }
    return false;
  };

  const onGoogle = async () => {
    setBusy(true);
    setError(null);
    track("signup_google_clicked", { ref_source: refSource ?? "direct" });
    try {
      await signInWithPopup(auth, googleProvider);
      track("signup_google_success");
    } catch (err) {
      track("signup_google_failed", { code: (err as { code?: string })?.code ?? "unknown" });
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError("Inicia sesión con Google para crear tu cuenta.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const call = await api.createTenantSelfServe({
        country,
        taxIdFormat,
        taxId,
        personType,
        businessType,
        name: name.trim(),
        legalName: legalName.trim() || undefined,
        address: address.trim(),
        city: city.trim() || undefined,
        website: website.trim() || undefined,
        instagram: instagram.trim() || undefined,
        locations,
        ownerRole: ownerRole.trim() || undefined,
        ownerName: ownerName.trim() || undefined,
        phone: phone.trim() || undefined,
        refSource
      });
      await refreshClaims();
      track("signup_completed", {
        tenant_id: call.data.tenantId,
        ref_source: refSource ?? "direct",
        business_type: businessType,
        country,
        person_type: personType,
        locations,
        is_likely_business: !!taxValidation?.isLikelyBusiness
      });
      nav(`/bienvenido?tenant=${encodeURIComponent(call.data.tenantId)}`, { replace: true });
    } catch (err) {
      track("signup_failed", {
        code: (err as { code?: string })?.code ?? "unknown",
        ref_source: refSource ?? "direct"
      });
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Track avance entre pasos
  useEffect(() => {
    track("signup_step_viewed", { step });
  }, [step]);

  return (
    <main className="min-h-dvh bg-cream">
      <div className="container-page flex flex-col gap-6">
        <header className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Fideliza · Para comercios</p>
          <h1 className="mt-2 font-display text-3xl font-semibold text-leaf-950">
            Activa tu programa de fidelización
          </h1>
          <p className="mt-2 text-sm text-leaf-700">
            En menos de 1 minuto tu QR estará listo para imprimir.
          </p>
        </header>

        <ProgressBar step={step} />

        {step === 1 && (
          <section className="card flex flex-col gap-4">
            <p className="text-xs uppercase tracking-wider text-leaf-600">Paso 1 · Tu empresa</p>

            <div className="flex flex-col gap-2">
              <span className="text-sm text-leaf-700">Tipo</span>
              <div className="grid grid-cols-2 gap-2">
                <RadioCard
                  selected={personType === "juridica"}
                  onClick={() => setPersonType("juridica")}
                  label="Persona jurídica"
                  hint="Empresa, SpA, Ltda."
                />
                <RadioCard
                  selected={personType === "natural"}
                  onClick={() => setPersonType("natural")}
                  label="Persona natural"
                  hint="Con boletas / giro activo"
                />
              </div>
              {personType === "natural" && (
                <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
                  Aceptamos personas naturales con giro comercial activo en SII. Vamos a verificar
                  manualmente; tu cuenta queda activa mientras tanto.
                </p>
              )}
            </div>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">RUT</span>
              <input
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                placeholder="77.411.698-2"
                className="input"
                autoComplete="off"
                inputMode="text"
              />
              {taxId && taxValidation && !taxValidation.valid && (
                <span className="text-xs text-red-700">{taxValidation.reason}</span>
              )}
              {taxId && taxValidation?.valid && (
                <span className="text-xs text-leaf-700">✓ RUT válido</span>
              )}
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Tipo de negocio</span>
              <select
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                className="input"
              >
                {BUSINESS_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={!canAdvance(1)}
              className="btn-primary"
            >
              Continuar
            </button>
          </section>
        )}

        {step === 2 && (
          <section className="card flex flex-col gap-4">
            <p className="text-xs uppercase tracking-wider text-leaf-600">Paso 2 · Datos del negocio</p>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Nombre comercial</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="MatchaLab"
                className="input"
                required
                minLength={2}
              />
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Razón social (opcional)</span>
              <input
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Fresherb SpA"
                className="input"
              />
            </label>

            <div className="flex flex-col gap-2">
              <span className="text-sm text-leaf-700">Cuántos locales</span>
              <div className="grid grid-cols-3 gap-2">
                {(["1", "2-5", "6+"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setLocations(v)}
                    className={`rounded-2xl border-2 px-3 py-3 text-sm font-medium transition ${
                      locations === v
                        ? "border-leaf-700 bg-leaf-50 text-leaf-900"
                        : "border-leaf-100 bg-white text-leaf-700"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Dirección principal</span>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Triana 851, Providencia"
                className="input"
                required
              />
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Comuna / Ciudad (opcional)</span>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Providencia"
                className="input"
              />
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Web (opcional)</span>
              <input
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://matchalab.cl"
                className="input"
                inputMode="url"
              />
            </label>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Instagram (opcional)</span>
              <input
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                placeholder="@matchalabchile"
                className="input"
              />
            </label>

            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setStep(1)} className="btn-ghost flex-1">
                ← Atrás
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                disabled={!canAdvance(2)}
                className="btn-primary flex-1"
              >
                Continuar
              </button>
            </div>
          </section>
        )}

        {step === 3 && (
          <form onSubmit={onSubmit} className="card flex flex-col gap-4">
            <p className="text-xs uppercase tracking-wider text-leaf-600">Paso 3 · Tu cuenta</p>

            {!user ? (
              <>
                <p className="text-sm text-leaf-700">
                  Inicia sesión con Google para terminar de crear tu cuenta.
                </p>
                <button type="button" onClick={onGoogle} disabled={busy} className="btn-primary">
                  Continuar con Google
                </button>
              </>
            ) : (
              <>
                <div className="rounded-2xl bg-leaf-50 px-4 py-3 text-sm text-leaf-800">
                  Conectado como <span className="font-semibold">{user.email}</span>
                </div>

                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-leaf-700">Tu nombre</span>
                  <input
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    placeholder="Raimundo Burchardt"
                    className="input"
                  />
                </label>

                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-leaf-700">Tu rol</span>
                  <select
                    value={ownerRole}
                    onChange={(e) => setOwnerRole(e.target.value)}
                    className="input"
                  >
                    <option>Dueño</option>
                    <option>Socio</option>
                    <option>Gerente</option>
                    <option>Empleado autorizado</option>
                  </select>
                </label>

                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-leaf-700">Teléfono (opcional)</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+56 9 ..."
                    className="input"
                    inputMode="tel"
                  />
                </label>

                <p className="text-xs text-leaf-600">
                  Al crear la cuenta aceptas los Términos y la Política de Privacidad.
                </p>

                {error && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="btn-ghost flex-1"
                    disabled={busy}
                  >
                    ← Atrás
                  </button>
                  <button type="submit" disabled={busy} className="btn-primary flex-1">
                    {busy ? "Creando…" : "Crear cuenta"}
                  </button>
                </div>
              </>
            )}
          </form>
        )}

        <p className="text-center text-xs text-leaf-600">
          ¿Ya tienes cuenta?{" "}
          <button
            type="button"
            onClick={() => nav("/login")}
            className="underline-offset-4 hover:underline"
          >
            Inicia sesión
          </button>
        </p>
      </div>
    </main>
  );
}

function ProgressBar({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3].map((s) => (
        <div
          key={s}
          className={`h-1.5 flex-1 rounded-full transition ${
            s <= step ? "bg-leaf-700" : "bg-leaf-200"
          }`}
        />
      ))}
    </div>
  );
}

function RadioCard({
  selected,
  onClick,
  label,
  hint
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border-2 p-4 text-left transition ${
        selected
          ? "border-leaf-700 bg-leaf-50"
          : "border-leaf-100 bg-white hover:bg-leaf-50/50"
      }`}
    >
      <p className="text-sm font-semibold text-leaf-950">{label}</p>
      <p className="mt-1 text-xs text-leaf-600">{hint}</p>
    </button>
  );
}
